import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../style/EditUser.css";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState({
    id: "",
    username: "",
    role: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const BASE_URL = "https://ctmapi.zerlak.com/api/v1/settings/users";

  // Get user data
  const getUser = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/getById/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const data = await response.json();
      setUser({
        ...data,
        role: data.role?.toUpperCase() // Normalize role case
      });
      setError("");
    } catch (err) {
      setError("Failed to load user data. Please try again.");
      console.error("Error fetching user:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getUser();
    }
  }, [id]);

  // Update user
  const updateUser = async (e) => {
    e.preventDefault();

    if (!user.username.trim()) {
      setError("Username is required");
      return;
    }

    if (!user.role) {
      setError("Role is required");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify(user)
      });

      if (!response.ok) {
        throw new Error("Failed to update user");
      }

      navigate("/dashboard/view-users");
    } catch (err) {
      setError("Failed to update user. Please try again.");
      console.error("Error updating user:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: value
    }));
    setError(""); // Clear error when user starts typing
  };

  const handleCancel = () => {
    navigate("/dashboard/view-users");
  };

  if (loading && !user.username) {
    return (
      <div className="edit-user-container">
        <div className="edit-user-card">
          <div className="edit-user-header">
            <h2>Loading User...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-user-container">
      <div className="edit-user-card">
        {/* HEADER */}
        <div className="edit-user-header">
          <h2>Edit User</h2>
          <p>Update user information and role permissions</p>
        </div>

        {/* FORM */}
        <form onSubmit={updateUser} className={`edit-user-form ${loading ? 'loading' : ''}`}>
          
          {/* Error Message */}
          {error && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#dc2626',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {error}
            </div>
          )}

          {/* Username Field */}
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-input"
              value={user.username}
              onChange={handleInputChange}
              placeholder="Enter username"
              disabled={loading}
              required
            />
          </div>

          {/* Role Field */}
          <div className="form-group">
            <label htmlFor="role" className="form-label">
              Role
            </label>
            <select
              id="role"
              name="role"
              className="form-select"
              value={user.role}
              onChange={handleInputChange}
              disabled={loading}
              required
            >
              <option value="">Select Role</option>
              <option value="ADMIN">Admin</option>
              <option value="USER">User</option>
              <option value="EMPLOYEE">Employee</option>
            </select>
          </div>

          {/* User ID (Read-only) */}
          <div className="form-group">
            <label htmlFor="userId" className="form-label">
              User ID
            </label>
            <input
              type="text"
              id="userId"
              className="form-input"
              value={user.id}
              disabled
              style={{
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                cursor: 'not-allowed'
              }}
              readOnly
            />
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn-update"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update User'}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;